import { useState, useEffect, useMemo } from 'react'
import { useParams, Navigate, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { Menu } from 'lucide-react'

import { HomepageFooter } from '@/components/homepage/sections/HomepageFooter'
import { DocsNavbar } from '@/components/docs/DocsNavbar'
import { DocsSidebar } from '@/components/docs/DocsSidebar'
import { DocsContent } from '@/components/docs/DocsContent'
import { categories } from './_data'
import { OG_IMAGE_URL, siteUrl } from '@/lib/config/site'

export default function DocsPage() {
  const { slug } = useParams()
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const flatArticles = useMemo(
    () => categories.flatMap((c) => c.articles),
    []
  )

  const activeArticle = flatArticles.find((a) => a.slug === slug)

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1))
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' })
        return
      }
    }
    window.scrollTo(0, 0)
  }, [slug, location.hash])

  useEffect(() => {
    setMobileMenuOpen(false)
  }, [slug])

  if (!slug || !activeArticle) {
    return <Navigate to="/docs/quick-start" replace />
  }

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Helmet>
        <title>{activeArticle.title} — Footage Documentation</title>
        <meta name="description" content={activeArticle.description} />
        <link rel="canonical" href={siteUrl(`/docs/${activeArticle.slug}`)} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${activeArticle.title} — Footage Documentation`} />
        <meta property="og:description" content={activeArticle.description} />
        <meta property="og:url" content={siteUrl(`/docs/${activeArticle.slug}`)} />
        <meta property="og:image" content={OG_IMAGE_URL} />
        <meta property="og:site_name" content="Footage" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${activeArticle.title} — Footage Documentation`} />
        <meta name="twitter:description" content={activeArticle.description} />
        <meta name="twitter:image" content={OG_IMAGE_URL} />
      </Helmet>

      <DocsNavbar />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex min-h-[calc(100vh-4rem)]">
          <DocsSidebar
            categories={categories}
            mobileOpen={mobileMenuOpen}
            onMobileClose={() => setMobileMenuOpen(false)}
          />
          <div className="flex-1 min-w-0">
            <div className="lg:hidden sticky top-16 z-30 bg-background/95 backdrop-blur-sm border-b border-border/50 px-4 py-2.5 flex items-center gap-2">
              <button onClick={() => setMobileMenuOpen(true)} className="p-1 -ml-1 rounded-md hover:bg-muted transition-colors">
                <Menu className="h-4 w-4" />
              </button>
              <span className="text-sm font-medium truncate">{activeArticle.title}</span>
            </div>
            <DocsContent article={activeArticle} allArticles={flatArticles} />
          </div>
        </div>
        <HomepageFooter />
      </div>
    </div>
  )
}
